import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getSupabaseClient } from '@/lib/supabase'
import { getSupabaseUrl } from '@/lib/runtime-env'
import { z, type Database, type ProductRole } from '@dcp/shared'

/**
 * Status of an organization invitation.
 *
 * 'processing' is a transient claim state set by the edge function during
 * accept (between the atomic claim and the final 'accepted' commit). End
 * users normally never see it — rows leave 'processing' within milliseconds.
 * Admin UI may briefly observe it for genuinely-in-flight rows, or for rows
 * stuck after an edge-function crash (recovered manually via SQL — see
 * migration 20260506000001).
 */
export type InvitationStatus = 'open' | 'processing' | 'accepted' | 'expired' | 'cancelled'

/**
 * Type of invitation
 */
export type InviteType = 'organization' | 'admin' | 'user'

/** Invite type for team-member (product-role) invitations. */
export const USER_INVITE_TYPE: InviteType = 'user'

/**
 * Organization invitation data structure
 */
export interface OrganizationInvitation {
  id: string
  organization_name: string
  admin_email: string
  admin_first_name: string | null
  admin_last_name: string | null
  status: InvitationStatus
  invite_type: InviteType
  invitation_token: string
  expires_at: string
  invited_by: string
  accepted_at: string | null
  created_organization_id: string | null
  created_at: string
  updated_at: string
  // For user invitations - target organization
  target_organization_id: string | null
  /**
   * Feature-level role assigned at invite time (user invitations only).
   * NULL for legacy organization/admin invite_types which predate the product_role feature.
   */
  product_role: string | null
  /** Bumped each resend; DCP-139 idempotency key version. */
  resend_count: number
  /**
   * Whether the invited address already has an account (DCP-350). Returned by
   * `get-by-token` only, so the accept page can ask an existing user to SIGN IN
   * and confirm rather than offering to create an account that cannot be
   * created — Supabase answers 422 email_exists for a taken address.
   *
   * Optional because every other endpoint returns the plain invitation row.
   */
  user_exists?: boolean
}

/**
 * Parameters for creating a new invitation
 */
export interface CreateInvitationParams {
  organization_name: string
  admin_email: string
  admin_first_name?: string
  admin_last_name?: string
}

/**
 * Parameters for creating an admin invitation (multi-org access)
 */
export interface CreateAdminInvitationParams {
  admin_email: string
  admin_first_name?: string
  admin_last_name?: string
  assigned_organization_ids: string[]
}

/**
 * Parameters for creating a user invitation (single org, product_role required).
 * product_role determines the feature-level permissions granted inside the tenant;
 * it is forwarded verbatim to the create-user edge function action.
 */
export interface CreateUserInvitationParams {
  admin_email: string
  admin_first_name?: string
  admin_last_name?: string
  target_organization_id: string
  /** Feature-level role assigned to the invited user within the organization. */
  product_role: ProductRole
}

/**
 * Parameters for accepting an invitation
 */
export interface AcceptInvitationParams {
  token: string
  password: string
}

/**
 * Organization data with admin info
 */
export interface Organization {
  id: string
  name: string
  status: Database['public']['Enums']['organization_status']
  suspended_at: string | null
  created_at: string
  updated_at: string
  admin_email: string | null
  admin_name: string | null
}

/**
 * Safely parses JSON from a response, returning null if parsing fails
 * Handles cases where the server returns non-JSON responses (HTML error pages, etc.)
 */
async function safeParseJson(response: Response): Promise<unknown> {
  try {
    const text = await response.text()
    return text ? JSON.parse(text) : null
  } catch {
    return null
  }
}

/**
 * Extracts error message from a parsed JSON response or returns default
 */
function getErrorMessage(json: unknown, defaultMessage: string): string {
  if (json && typeof json === 'object' && 'error' in json) {
    return String((json as { error: unknown }).error) || defaultMessage
  }
  return defaultMessage
}

/**
 * Query keys for invitation-related data
 */
const INVITATION_KEYS = {
  all: ['invitations'] as const,
  /** Under `all`, so every invitation mutation's invalidation refreshes the page too. */
  page: (query: InvitationsPageQuery) => ['invitations', 'page', query] as const,
  byToken: (token: string) => ['invitations', 'token', token] as const,
  organizations: ['organizations'] as const,
  /**
   * Under `organizations`, so the suspend/unsuspend invalidation
   * (`useOrganizationActions`, same `['organizations']` root) refreshes it too.
   */
  organizationsPage: (page: number, pageSize: number) => ['organizations', 'page', { page, pageSize }] as const,
}

/** Root segment of the unified team-invitations cache key. */
const UNIFIED_TEAM_INVITATIONS_KEY = 'unified-team-invitations'

/**
 * Query keys for the unified team-member list's invitation slice
 * (the cache read by useOrgInvitationsAll in useTeamMembers).
 *
 * Centralized here so the invitation mutations below and the reader share a
 * single source of truth — query-key drift between writer and reader is exactly
 * what caused DCP-236.
 *
 * `all` is the broad root key: React Query prefix-matches it against every
 * org's scoped `invitations(orgId)` entry, so one invalidate refreshes the
 * active org's table without each mutation needing to know the orgId.
 */
export const UNIFIED_TEAM_INVITATIONS_KEYS = {
  all: [UNIFIED_TEAM_INVITATIONS_KEY] as const,
  invitations: (orgId?: string) => [UNIFIED_TEAM_INVITATIONS_KEY, orgId] as const,
}

/**
 * Hook to fetch an invitation by token
 * Used on the accept invitation page to display invitation details
 * @param token - Invitation token from URL
 */
export function useInvitationByToken(token: string) {
  return useQuery({
    queryKey: INVITATION_KEYS.byToken(token),
    queryFn: async () => {
      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'get-by-token',
            token,
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to fetch invitation'))
      }

      const result = await safeParseJson(response)
      if (!result || typeof result !== 'object' || !('data' in result)) {
        throw new Error('Invalid response format from server')
      }
      return (result as { data: OrganizationInvitation }).data
    },
    enabled: !!token,
  })
}

/**
 * Hook to create a new organization invitation
 * Sends an invitation email to the specified admin user
 */
export function useCreateInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: CreateInvitationParams) =>
      postInvitationsAction({ action: 'create', ...params }, 'Failed to create invitation'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.all })
    },
  })
}

/**
 * Hook to create an admin invitation
 * Sends an invitation email to an admin with access to specified organizations
 */
export function useCreateAdminInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: CreateAdminInvitationParams) =>
      postInvitationsAction({ action: 'create-admin', ...params }, 'Failed to create admin invitation'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.all })
    },
  })
}

/**
 * Hook to create a user invitation for a specific organization
 * Creates a tech_team user bound to the target organization
 */
export function useCreateUserInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: CreateUserInvitationParams) =>
      postInvitationsAction({ action: 'create-user', ...params }, 'Failed to create user invitation'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.all })
      queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
    },
  })
}

/**
 * Hook to resend an existing invitation
 * Sends a new invitation email with the same token
 * @param invitation_id - ID of the invitation to resend
 */
export function useResendInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invitation_id: string) =>
      postInvitationsAction({ action: 'resend', invitation_id }, 'Failed to resend invitation'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.all })
      queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
    },
  })
}

/**
 * Hook to cancel an open invitation
 * Sets the invitation status to 'cancelled'
 * @param invitation_id - ID of the invitation to cancel
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invitation_id: string) =>
      postInvitationsAction({ action: 'cancel', invitation_id }, 'Failed to cancel invitation'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INVITATION_KEYS.all })
      queryClient.invalidateQueries({ queryKey: UNIFIED_TEAM_INVITATIONS_KEYS.all })
    },
  })
}

/**
 * Hook to accept an organization invitation
 * Creates a new user account and organization, marks invitation as accepted
 * This is a public endpoint that doesn't require authentication
 */
export function useAcceptInvitation() {
  return useMutation({
    mutationFn: async (params: AcceptInvitationParams) => {
      const response = await fetch(
        `${getSupabaseUrl()}/functions/v1/organization-invitations`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'accept',
            token: params.token,
            password: params.password,
          }),
        }
      )

      if (!response.ok) {
        const errorJson = await safeParseJson(response)
        throw new Error(getErrorMessage(errorJson, 'Failed to accept invitation'))
      }

      return safeParseJson(response)
    },
  })
}

/**
 * Accept an invitation with an account the user ALREADY has (DCP-350).
 *
 * Separate from {@link useAcceptInvitation} because the two are different
 * operations, not two shapes of one. The ordinary path is anonymous and sends a
 * password — it CREATES the account. This one sends the session token and no
 * password: the account exists, and proving ownership of it is exactly what
 * stops a leaked invitation link from attaching an organization to somebody
 * else's account. The server rejects the call unless the signed-in user is the
 * invited address.
 *
 * The caller must therefore be signed in before this runs; the accept page
 * signs them in first, then calls this.
 */
export function useAcceptInvitationAsExistingUser() {
  return useMutation({
    mutationFn: (params: { token: string }) =>
      postInvitationsAction(
        { action: 'accept-existing', token: params.token },
        'Failed to accept invitation',
        'Please sign in to accept this invitation',
      ),
  })
}

/**
 * Hook to fetch all organizations (admin only)
 */
export function useOrganizations() {
  return useQuery({
    queryKey: INVITATION_KEYS.organizations,
    queryFn: async () => {
      const result = await postInvitationsAction({ action: 'list-organizations' }, 'Failed to fetch organizations')
      if (!result || typeof result !== 'object' || !('data' in result)) {
        throw new Error('Invalid response format from server')
      }
      return (result as { data: Organization[] }).data
    },
  })
}

/** One page of a server-paged admin list: the requested page of the filtered rows. */
export interface ServerPageRequest {
  /** 1-based page. */
  page: number
  pageSize: number
}

/** Filters and page of {@link useInvitationsPage}. */
export interface InvitationsPageQuery extends ServerPageRequest {
  inviteType: InviteType
  /** `undefined` = every status. */
  status: InvitationStatus | undefined
  /** Matched against organization name and invitee email; blank = no search. */
  search: string
}

/** Invitation tallies for the caller's scope and invite type, ignoring status and search. */
export interface InvitationCounts {
  total: number
  open: number
  accepted: number
  expired: number
}

export interface InvitationsPage {
  invitations: OrganizationInvitation[]
  /** Rows matching the filters across all pages. */
  total: number
  counts: InvitationCounts
}

export interface OrganizationsPage {
  organizations: Organization[]
  /** Organizations across all pages. */
  total: number
}

const invitationSchema: z.ZodType<OrganizationInvitation> = z.object({
  id: z.string(),
  organization_name: z.string(),
  admin_email: z.string(),
  admin_first_name: z.string().nullable(),
  admin_last_name: z.string().nullable(),
  status: z.enum(['open', 'processing', 'accepted', 'expired', 'cancelled']),
  invite_type: z.enum(['organization', 'admin', 'user']),
  invitation_token: z.string(),
  expires_at: z.string(),
  invited_by: z.string(),
  accepted_at: z.string().nullable(),
  created_organization_id: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  target_organization_id: z.string().nullable(),
  product_role: z.string().nullable(),
  resend_count: z.number(),
  user_exists: z.boolean().optional(),
})

const organizationSchema: z.ZodType<Organization> = z.object({
  id: z.string(),
  name: z.string(),
  status: z.enum(['active', 'suspended', 'inactive']),
  suspended_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  admin_email: z.string().nullable(),
  admin_name: z.string().nullable(),
})

const count = z.number().int().nonnegative()

const invitationsPageResponseSchema = z.object({
  data: z.object({
    invitations: z.array(invitationSchema),
    total: count,
    counts: z.object({ total: count, open: count, accepted: count, expired: count }),
  }),
})

const organizationsPageResponseSchema = z.object({
  data: z.object({
    organizations: z.array(organizationSchema),
    total: count,
  }),
})

/**
 * POSTs an action to the `organization-invitations` edge function with the
 * session's bearer token and returns the parsed JSON body — the one
 * authenticated call path for this function. Without a session it throws
 * `signedOutMessage`; a non-2xx answer throws the function's `error` text, or
 * `failureMessage` when there is none. The anonymous actions (`get-by-token`,
 * `accept`) send no bearer and do not come through here.
 */
async function postInvitationsAction(
  body: Record<string, unknown>,
  failureMessage: string,
  signedOutMessage = 'Authentication required - please log in',
): Promise<unknown> {
  const {
    data: { session },
  } = await getSupabaseClient().auth.getSession()
  if (!session?.access_token) {
    throw new Error(signedOutMessage)
  }

  const response = await fetch(`${getSupabaseUrl()}/functions/v1/organization-invitations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  })
  const json = await safeParseJson(response)
  if (!response.ok) {
    throw new Error(getErrorMessage(json, failureMessage))
  }
  return json
}

/** The `data` of a response that must match `schema`; a mismatch is a server/client contract bug. */
function parseResponse<T>(schema: z.ZodType<{ data: T }>, json: unknown): T {
  const parsed = schema.safeParse(json)
  if (!parsed.success) {
    const issue = parsed.error.issues[0]
    throw new Error(`Invalid response format from server (${issue.path.join('.')}: ${issue.message})`)
  }
  return parsed.data.data
}

function pageBounds({ page, pageSize }: ServerPageRequest) {
  return { limit: pageSize, offset: (page - 1) * pageSize }
}

/**
 * One server-side page of invitations of one invite type (the admin
 * Invitations table), filtered by status and search, with the exact filtered
 * total and the status tallies for the stat cards. The edge function scopes
 * rows by the caller's role.
 *
 * Settings → Team does not page: it reads the unpaged `list` action through
 * its own hooks (`useTeamMembers`, `useTeamManagement`). The previous page
 * stays on screen while the next one loads; the data is platform-wide (not
 * organization-scoped), so no org guard is needed.
 */
export function useInvitationsPage(query: InvitationsPageQuery) {
  const normalized: InvitationsPageQuery = { ...query, search: query.search.trim() }
  return useQuery({
    queryKey: INVITATION_KEYS.page(normalized),
    queryFn: async (): Promise<InvitationsPage> => {
      const json = await postInvitationsAction(
        {
          action: 'list',
          invite_type: normalized.inviteType,
          ...(normalized.status && { status: normalized.status }),
          ...(normalized.search && { search: normalized.search }),
          ...pageBounds(normalized),
        },
        'Failed to fetch invitations',
      )
      return parseResponse(invitationsPageResponseSchema, json)
    },
    placeholderData: keepPreviousData,
  })
}

/**
 * One server-side page of organizations (super_admin), ordered by name, each
 * with its owner's email and name, plus the exact total.
 *
 * The admin-invite organization selector keeps using {@link useOrganizations}
 * (the full list). The previous page stays on screen while the next one loads.
 */
export function useOrganizationsPage({ page, pageSize }: ServerPageRequest) {
  return useQuery({
    queryKey: INVITATION_KEYS.organizationsPage(page, pageSize),
    queryFn: async (): Promise<OrganizationsPage> => {
      const json = await postInvitationsAction(
        { action: 'list-organizations', ...pageBounds({ page, pageSize }) },
        'Failed to fetch organizations',
      )
      return parseResponse(organizationsPageResponseSchema, json)
    },
    placeholderData: keepPreviousData,
  })
}
